import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const HairCubedBlogPost: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();

  return (
    <article className="min-h-screen bg-background-primary py-16">
      <div className="container max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm font-medium text-primary-color bg-primary-color/10 px-3 py-1.5 rounded-full">
              UX Audit
            </span>
            <span className="text-sm text-text-secondary">
              6 min read
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            UX Audit of Hair Cubed's Product Page (Mobile First Fold)
          </h1>
          <p className="text-xl text-text-secondary">
            An in-depth UX audit of HairCubed's product page, focused on accessibility, information hierarchy, and mobile usability.
          </p>
          <p className="text-xl text-text-secondary mt-4">
            I identified friction points in the first fold and proposed a redesigned layout that prioritizes essential product details, improves accessibility, and reduces purchase flow friction.
          </p>
        </div>

        {/* Cover Image */}
        <div className="relative w-full h-[400px] md:h-[500px] rounded-xl overflow-hidden mb-12">
          <img
            src="/images/projects/covers/cover-image-hair-cubed-ux-audit.png"
            alt="Hair Cubed Product Page UX Audit Cover"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Content */}
        <div className="max-w-none space-y-12">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About HairCubed</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              HairCubed is a U.S.-based personal care brand specializing in micro-fiber solution designed to address thinning hair, fine hair, bald spots, and hair loss. This is a UX Audit conducted on the first fold of HC's{' '}
              <a 
                href="https://haircubed.com/products/haircubed-microfiber-sealer-control-ionic-brush" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary-color hover:text-primary-color/80 underline"
              >
                product page
              </a>{' '}
              for mobile.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Identifying Accessibility and UX Issues</h2>
            <div className="text-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                The "Best Seller" and "Promotions" labels are currently embedded inside the product images. This approach looks unprofessional and, more importantly, is not accessible for screen readers.
              </p>
              <p>
                Additionally, key product details (color selection, customer reviews) are also integrated directly into the images, which creates two problems:
              </p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>They are unreadable for text-to-speech tools, harming accessibility.</li>
                <li>They are harder to maintain, increasing the risk of inconsistent product information across the site.</li>
              </ol>
              <p>
                Another issue is the first fold layout. On mobile, the entire first fold is occupied by product images, meaning users must either swipe through the carousel or scroll down before they can find essential information such as the product title, price, or color options. This extra interaction introduces unnecessary friction in the purchase flow, especially for new users who expect quick access to core product details.
              </p>
              <p>
                The product page is also cluttered with promotional messages (in the header, images, descriptions, and price sections). This repetition reduces clarity and can overwhelm users.
              </p>
              <p>
                Finally, the overall UI feels outdated. While this might comfort existing customers, it risks alienating younger users who expect modern, intuitive design.
              </p>
              
              {/* Issue illustration image */}
              <div className="mt-8 mb-4">
                <img 
                  src="/images/projects/detail/hair-cubed-ux-audit/part-1.png" 
                  alt="Hair Cubed product page accessibility and UX issues illustration"
                  className="w-full rounded-lg"
                />
                <p className="text-base text-gray-600 mt-3 italic">
                  Current implementation: Labels and key product information embedded in images. The first fold on mobile is fully occupied by the product images; the user needs to scroll down to access the product details.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Defining User Assumptions</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              To frame the redesign, I established assumptions about the target user group:
            </p>
            <ul className="text-lg text-gray-700 leading-relaxed space-y-6">
              <li className="flex items-start">
                <span className="text-primary-color mr-2">•</span>
                <div>
                  <span>Primary device: Mobile phones.</span>
                  <ul className="mt-2 ml-6">
                    <li className="flex items-start">
                      <span className="text-primary-color/60 mr-2">•</span>
                      <span>Smartphones accounted for 77% of retail site traffic globally in Q3 2024, generating 68% of online shopping orders. (Statista, 2024)</span>
                    </li>
                  </ul>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-color mr-2">•</span>
                <div>
                  <span>Primary OS: iOS.</span>
                  <ul className="mt-2 ml-6">
                    <li className="flex items-start">
                      <span className="text-primary-color/60 mr-2">•</span>
                      <span>In 2025, iOS devices hold 58% of the U.S. smartphone market, compared to Android's 42%. (Backlinko, 2025)</span>
                    </li>
                  </ul>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-color mr-2">•</span>
                <div>
                  <span>Target audience: Men and women experiencing hair loss.</span>
                  <ul className="mt-2 ml-6">
                    <li className="flex items-start">
                      <span className="text-primary-color/60 mr-2">•</span>
                      <span>By age 35, two-thirds of American men show some degree of hair loss; by age 50, this rises to 85%. (Healthline, 2024)</span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Problem: Suboptimal First Fold on Mobile</h2>
            <div className="text-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                On mobile, the first fold (content visible without scrolling) heavily impacts UX performance. If essential product information isn't accessible at first glance, users may:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Misunderstand the product.</span>
                </li>
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Fail to find key details.</span>
                </li>
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Abandon the purchase flow.</span>
                </li>
              </ul>
              <p>In its current state, the first fold:</p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Is confusing.</span>
                </li>
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Does not prioritize essential information.</span>
                </li>
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Misleads users with mismatched visual cues (e.g. photo-based ratings vs. actual site ratings).</span>
                </li>
              </ul>
              <p>
                Optimizing the first fold for clarity and hierarchy could directly improve conversions, especially given the predominance of mobile/iOS users.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Considering Browser UI Constraints</h2>
            <div className="text-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                On mobile, browser UI elements (search bars, navigation controls) occupy valuable screen space. This reduces the visible portion of a product page compared to a native app.
              </p>
              <p>
                To account for this, I analyzed the "above the fold" area across the three most-used browsers in the U.S.:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Google Chrome</span>
                </li>
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Apple Safari</span>
                </li>
                <li className="flex items-center">
                  <span className="text-primary-color mr-2">•</span>
                  <span>Microsoft Edge</span>
                </li>
              </ul>
              <p>
                This ensured my redesign prioritized content that would reliably appear across most iPhone screen sizes, regardless of browser UI.
              </p>
              
              {/* Browser UI comparison image */}
              <div className="mt-8 mb-4">
                <img 
                  src="/images/projects/detail/hair-cubed-ux-audit/browser-comparison.png" 
                  alt="Browser UI comparison showing different visible areas"
                  className="w-full rounded-lg"
                />
                <p className="text-base text-gray-600 mt-3 italic">
                  Unlike mobile apps, browsers reduce available screen space with their UI and status bars. Apple recommends safe areas of 44–47pt (top) and 34pt (bottom). The visible first fold varies by browser: Safari offers the most space, followed by Edge and Chrome.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Prioritizing Key Elements</h2>
            <div className="text-lg text-gray-700 leading-relaxed space-y-4">
              <p>The first fold must highlight elements that drive initial purchase decisions:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Product images</li>
                <li>Title</li>
                <li>Price</li>
                <li>Option availability (e.g., color, size)</li>
                <li>Primary CTA (Add to Cart)</li>
              </ol>
              <p>
                Secondary elements (labels, discounts, reviews) serve as nudges, while utility elements (quantity selector, back navigation, cart icon) support a seamless flow.
              </p>

              {/* Key elements illustration */}
              <div className="mt-8">
                <img 
                  src="/images/projects/detail/hair-cubed-ux-audit/mobile-app.png" 
                  alt="Mobile app key elements prioritization illustration"
                  className="w-full rounded-lg max-h-[700px] object-contain"
                />
                <p className="text-base text-gray-600 mt-3 italic">
                  Prioritizing essential elements in the first fold ensures users can make quick purchase decisions without unnecessary scrolling.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Redesign Decisions</h2>
            <div className="text-lg text-gray-700 leading-relaxed space-y-4">
              <p>Based on the analysis, I restructured the first fold with a minimalist, user-centered layout:</p>
              <ul className="space-y-6">
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Essential elements displayed first:</span> Images, title, reviews, color options, price + discount, "Best Seller" label, Add to Cart button.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Sticky Add to Cart button:</span> Anchored at the bottom-right, within natural thumb reach, with the price always visible.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Professionalized image carousel:</span>
                    <ul className="mt-2 ml-6 space-y-2">
                      <li className="flex items-start">
                        <span className="text-primary-color/60 mr-2">•</span>
                        <span>Labels and text removed from product images.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary-color/60 mr-2">•</span>
                        <span>A floating "Best Seller" tag remains visible but disappears when images are expanded.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-primary-color/60 mr-2">•</span>
                        <span>Carousel uses pagination indicators instead of a static row of thumbnails.</span>
                      </li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Simplified product title:</span> Removed redundant brand name ("HairCubed"), as product images already communicate it.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Rating positioned under title:</span> Prioritized as a trust-building element.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Review submission link removed:</span> Deferred lower in the page, as it is not critical at first glance.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Expandable color options:</span> Initial six displayed, expandable for more.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Streamlined header:</span> Removed logo and promotional bar to reclaim vertical space. The discount remains visible next to the Add to Cart button.
                  </div>
                </li>
              </ul>

              {/* Final redesign illustration */}
              <div className="mt-8 mb-4">
                <img 
                  src="/images/projects/detail/hair-cubed-ux-audit/final-redesign.png" 
                  alt="Final redesign of Hair Cubed's product page"
                  className="w-full rounded-lg"
                />
                <p className="text-base text-gray-600 mt-3 italic">
                  The redesigned layout prioritizes essential information and maintains a clean, modern aesthetic that aligns with current mobile design standards.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Outcome</h2>
            <div className="text-lg text-gray-700 leading-relaxed space-y-4">
              <p>The redesigned first fold delivers a clean, modern, and conversion-focused layout:</p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Accessibility improved:</span> Text content is decoupled from images.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Information hierarchy clarified:</span> Essentials visible at first glance.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Mobile usability optimized:</span> CTA within thumb reach, clutter minimized.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-color mr-2">•</span>
                  <div>
                    <span className="font-medium">Conversion potential strengthened:</span> Sticky CTA, visible discount, persistent Best Seller label.
                  </div>
                </li>
              </ul>
              <p>
                The result is a first fold that feels trustworthy, easy to navigate, and deliberately "too easy" to buy from — reducing friction and nudging users toward conversion.
              </p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-primary-color hover:text-primary-color/80 transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </article>
  );
};

export default HairCubedBlogPost;
